import { useT } from '../i18n/locale-context';
import type { GoodStanding, GoodStandingTier } from '../lib/catalog-client';

/**
 * The composite «good standing» trust badge (cluster-2 directory).
 *
 * A TRANSPARENT tier, not a score. The label is rendered from the `tier` via
 * i18n (so it localizes to uz/ru) — the API also ships a Russian `label`, but
 * we prefer the locale-correct one here. `unknown` (or absent) renders nothing:
 * we never show a status claim we can't back.
 */
const TIER_STYLE: Record<Exclude<GoodStandingTier, 'unknown'>, string> = {
  debarred:
    'border-status-closing-text/40 bg-status-closing-bg text-status-closing-text font-semibold',
  liquidated: 'border-status-closing-text/25 bg-status-closing-bg text-status-closing-text',
  // KZ-only (КГД register hit) — same visual weight as `liquidated`, one step
  // below `debarred`'s bold treatment since it isn't a procurement sanction.
  flagged: 'border-status-closing-text/25 bg-status-closing-bg text-status-closing-text',
  active_supplier: 'border-status-active-text/25 bg-status-active-bg text-status-active-text',
  active: 'border-edge bg-bone-surface text-ink-muted',
};

export function GoodStandingBadge({
  goodStanding,
  className = '',
  kind = 'supplier',
  onBrand = false,
}: {
  goodStanding: GoodStanding | null | undefined;
  className?: string;
  /** Which entity the badge sits on. Only the `active_supplier` tier is
   *  role-specific — on a buyer (заказчик) «Действующий поставщик» is a category
   *  error, so it reads «Действующий заказчик» instead. Every other tier
   *  (debarred / liquidated / flagged / active) is entity-neutral. */
  kind?: 'supplier' | 'buyer';
  /** On the brand-teal profile header the light-surface TIER_STYLE tokens
   *  (bone bg / ink text) collapse — same trap the WatchButton comment flags.
   *  `onBrand` swaps to a white-alpha pill with a severity-coloured status dot
   *  (green = active, amber = debarred/liquidated), matching the header idiom. */
  onBrand?: boolean;
}) {
  const t = useT();
  if (!goodStanding || goodStanding.tier === 'unknown') return null;
  const tier = goodStanding.tier;
  const label =
    kind === 'buyer' && tier === 'active_supplier'
      ? t('goodStanding.tierBuyer.active_supplier')
      : t(`goodStanding.tier.${tier}`);
  const alarming = tier === 'debarred' || tier === 'liquidated' || tier === 'flagged';

  if (onBrand) {
    return (
      <span
        className={`inline-flex items-center gap-1.5 rounded-pill border border-white/20 bg-white/12 px-2.5 py-0.5 text-[12px] font-semibold leading-tight text-white ${className}`}
      >
        <span
          aria-hidden
          className={`h-1.5 w-1.5 rounded-full ${alarming ? 'bg-orange' : 'bg-status-active-dot'}`}
        />
        {label}
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-pill border px-2.5 py-0.5 text-[12px] leading-tight ${TIER_STYLE[tier]} ${className}`}
    >
      {alarming && <span aria-hidden>⚠</span>}
      {label}
    </span>
  );
}
